import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    AlertTriangle,
    BookOpen,
    Calendar,
    Check,
    CheckCircle2,
    ChevronDown,
    Copy,
    ExternalLink,
    Info,
    Loader2,
    Mail,
    Minus,
    Plus,
    Search,
    X
} from "lucide-react";

import { useDemoMode } from "../../../context/DemoModeContext";
import { AiVisibilityModalOverlay } from "../AiVisibilityModalOverlay";

import { KEY_EVENTS } from "./fixtures/attribution-demo";
import { OAUTH_ERROR_CODES } from "./fixtures/oauth-demo";
import { AI_SOURCE_REGEX, COPY } from "./copy";
import type { AiChannelEvaluation, Ga4RevenueSource, Ga4SetupDetails } from "./ga4Api";
import { evaluateAiChannel, fetchGa4ChannelGroups, readRefreshToken } from "./ga4Api";
import Ga4Connect from "./Ga4Connect";
import Ga4LivePropertyPicker from "./Ga4LivePropertyPicker";
import MockGoogleOAuth, { DEFAULT_OAUTH_SCOPES } from "./MockGoogleOAuth";
import {
    RevenueStackComingSoon,
    RevenueStackScriptStep,
    RevenueStackSessionIdStep,
    RevenueStackStripeStep
} from "./RevenueStackSteps";
import type { ChannelOutcome, SetupPath, SetupState } from "./setupState";
import { CURRENCIES, currencySymbol, mockDelay } from "./setupState";
import type { StepId } from "./wizardSteps";
import { stepRowsFor } from "./wizardSteps";

import "./RevenueAttributionSetupWizardV2.scss";

// The step model is shared with the page's OAuth return legs — re-export so callers
// have a single import surface for the v2 wizard.
export { stepIndexOf, stepRowsFor } from "./wizardSteps";

/** Deep link into the customer's own GA4 admin, so "fix it" is one click away. */
const GA4_ADMIN_URL = "https://analytics.google.com/analytics/web/#/admin";

/** TODO: point at the real setup documentation once it's published. */
const DOCS_URL = "#";

// ── small primitives ─────────────────────────────────────────────────────────

/** Renders **bold**, *italic* and `code` inline without a markdown dependency. */
function RichText({ text }: { text: string }) {
    const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g).filter(Boolean);
    return (
        <>
            {parts.map((part, i) => {
                if (part.startsWith("**") && part.endsWith("**")) {
                    return <strong key={i}>{part.slice(2, -2)}</strong>;
                }
                if (part.startsWith("`") && part.endsWith("`")) return <code key={i}>{part.slice(1, -1)}</code>;
                if (part.startsWith("*") && part.endsWith("*")) {
                    return (
                        <em key={i} className="italic">
                            {part.slice(1, -1)}
                        </em>
                    );
                }
                return <React.Fragment key={i}>{part}</React.Fragment>;
            })}
        </>
    );
}

const Arrow = () => (
    <svg className="arw" width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden>
        <path
            d="M3 8h9M9 4l4 4-4 4"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

function usePrefersReducedMotion(): boolean {
    const [reduced, setReduced] = useState(false);
    useEffect(() => {
        if (typeof window === "undefined" || !window.matchMedia) return;
        const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
        const apply = () => setReduced(mq.matches);
        apply();
        mq.addEventListener("change", apply);
        return () => mq.removeEventListener("change", apply);
    }, []);
    return reduced;
}

/** Editorial header: eyebrow · balanced title · lede. Shared by every step. */
function StepIntro({ eyebrow, title, lede }: { eyebrow: string; title: string; lede: string }) {
    return (
        <div className="rasx-intro">
            <div className="rasx-intro-eyebrow">{eyebrow}</div>
            <h2>{title}</h2>
            <p className="rasx-lede">{lede}</p>
        </div>
    );
}

/**
 * A single "here's what GA4 has" line for the setup checks — replaces the older
 * "what we need / what we found" pair. Leads with the finding, coloured by meaning:
 * ok (green), warn (amber, something to fix), none (neutral, nothing found yet).
 */
function CheckResult({
    tone,
    pill,
    loading = false,
    children
}: {
    tone: "ok" | "warn" | "none";
    pill?: string;
    loading?: boolean;
    children: React.ReactNode;
}) {
    const icon = loading ? (
        <Loader2 className="rasx-spin" size={14} strokeWidth={2.5} />
    ) : tone === "ok" ? (
        <Check size={14} strokeWidth={3} />
    ) : tone === "warn" ? (
        <AlertTriangle size={13} strokeWidth={2.5} />
    ) : (
        <Minus size={14} strokeWidth={3} />
    );
    return (
        <div className={`rasx-result ${tone}`}>
            <span className="rasx-result-ic">{icon}</span>
            <span className="rasx-result-text">{children}</span>
            {pill && <span className="rasx-result-pill">{pill}</span>}
        </div>
    );
}

function GaAdminLink({ label }: { label: string }) {
    return (
        <a className="rasx-link" href={GA4_ADMIN_URL} target="_blank" rel="noreferrer noopener">
            {label}
            <ExternalLink />
        </a>
    );
}

/** Link to the setup docs. URL is a placeholder until the real guide is published. */
function DocsLink({ label = "Documentation" }: { label?: string }) {
    return (
        <a className="rasx-link muted" href={DOCS_URL} target="_blank" rel="noreferrer noopener">
            <BookOpen />
            {label}
        </a>
    );
}

// ── Path chooser ───────────────────────────────────────────────────────────────

/** Google Analytics 4 brand mark — the amber bars users recognise, drawn inline so
 *  there's no asset dependency. */
function Ga4Logo() {
    return (
        <svg viewBox="0 0 24 24" width="21" height="21" aria-hidden="true" focusable="false">
            <rect x="14.5" y="2.5" width="6" height="19" rx="3" fill="#F9AB00" />
            <rect x="8.5" y="8.5" width="6" height="13" rx="3" fill="#E8710A" />
            <circle cx="5.5" cy="18.5" r="3" fill="#E8710A" />
        </svg>
    );
}

/** ThriveStack tile — the product's own accent, so the second path reads as ours. */
function ThriveStackLogo() {
    return (
        <svg viewBox="0 0 24 24" width="21" height="21" aria-hidden="true" focusable="false">
            <rect width="24" height="24" rx="6" fill="#2F54EB" />
            <path
                d="M7 15.5 10.4 9l2.3 3.7L15 8l2 3.9"
                fill="none"
                stroke="#fff"
                strokeWidth="1.9"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

/** Product chrome shared by both previews — filter chips + tab rail, so the right side
 *  reads as a real screen of the app, not a floating widget. */
function PreviewChrome({ crumb, tab, tabs }: { crumb: React.ReactNode; tab: string; tabs: string[] }) {
    return (
        <>
            <div className="aw-top">
                <span className="aw-crumb">{crumb}</span>
                <span className="aw-preview-badge" title="Sample data — not your live report yet">
                    Preview
                </span>
            </div>
            <div className="aw-filters">
                <span className="aw-chip">
                    <Calendar size={11} strokeWidth={2} />
                    Last 30 days
                    <ChevronDown size={11} strokeWidth={2} />
                </span>
                <span className="aw-chip">
                    All AI assistants
                    <ChevronDown size={11} strokeWidth={2} />
                </span>
                <span className="aw-chip">
                    All countries
                    <ChevronDown size={11} strokeWidth={2} />
                </span>
            </div>
            <div className="aw-tabs">
                {tabs.map((t) => (
                    <span key={t} className={`aw-tab${t === tab ? " on" : ""}`}>
                        {t}
                    </span>
                ))}
            </div>
        </>
    );
}

/** Faint multi-series line chart — the "sessions over time" shape from the real report. */
function PreviewLines() {
    const lines = [
        { d: "M0,74 C46,66 78,40 120,46 168,53 206,26 248,32 292,38 312,34 340,30", c: "#7c5cfc" },
        { d: "M0,60 C46,58 84,56 120,57 170,58 210,55 252,56 300,57 320,56 340,55", c: "#2f54eb" },
        { d: "M0,88 C50,86 88,82 128,78 176,73 214,80 256,76 300,72 322,70 340,68", c: "#10a472" },
        { d: "M0,50 C44,54 82,62 124,64 172,66 212,58 254,52 298,46 320,44 340,42", c: "#e8710a" }
    ];
    return (
        <svg className="aw-chart" viewBox="0 0 340 110" preserveAspectRatio="none" aria-hidden="true">
            {[22, 44, 66, 88].map((y) => (
                <line key={y} className="aw-grid" x1="0" x2="340" y1={y} y2={y} />
            ))}
            {lines.map((l) => (
                <path key={l.c} className="aw-ln" d={l.d} style={{ stroke: l.c }} />
            ))}
        </svg>
    );
}

/** GA4 → what you actually get: a screen of the AI-analytics report. */
function Ga4Dashboard() {
    return (
        <div className="rasx-appwin" aria-hidden="true">
            <PreviewChrome
                crumb={
                    <>
                        Agent analytics <span className="aw-sep">›</span> <b>Google Analytics</b>
                    </>
                }
                tab="Overview"
                tabs={["Overview", "Details", "Settings"]}
            />
            <div className="aw-body">
                <div className="aw-src">linear.app · GA4 · Last 30 days</div>
                <div className="aw-headline">ChatGPT drove 45% of AI-referred sessions</div>
                <div className="aw-sub">12,324 sessions arrived from AI Assistants in the last 30 days.</div>
                <div className="aw-kpis">
                    <div className="aw-kpi">
                        <span className="k">Sessions</span>
                        <b>12,438</b>
                        <i className="up">+26.1%</i>
                    </div>
                    <div className="aw-kpi">
                        <span className="k">Engaged</span>
                        <b>68.2%</b>
                    </div>
                    <div className="aw-kpi">
                        <span className="k">Revenue</span>
                        <b>$29.5k</b>
                        <i className="up">+18.4%</i>
                    </div>
                </div>
                <div className="aw-card">
                    <div className="aw-card-h">Sessions over time</div>
                    <PreviewLines />
                </div>
            </div>
        </div>
    );
}

/** ThriveStack path → the payments-attribution screen it produces. */
function ThriveStackDashboard() {
    const rows = [
        { pg: "/pricing", src: "Perplexity", amt: "$2,682" },
        { pg: "/demo", src: "ChatGPT", amt: "$1,240" },
        { pg: "/guides/aeo", src: "Gemini", amt: "$998" }
    ];
    return (
        <div className="rasx-appwin" aria-hidden="true">
            <PreviewChrome
                crumb={
                    <>
                        Revenue <span className="aw-sep">›</span> <b>Attribution</b>
                    </>
                }
                tab="Overview"
                tabs={["Overview", "Pages", "Settings"]}
            />
            <div className="aw-body">
                <div className="aw-src">Stripe · ThriveStack · Last 30 days</div>
                <div className="aw-headline">$4,920 traced to AI-cited pages</div>
                <div className="aw-sub">Real payments matched to the page and assistant that earned them.</div>
                <div className="aw-kpis">
                    <div className="aw-kpi">
                        <span className="k">Attributed</span>
                        <b>$4,920</b>
                        <i className="up">+22%</i>
                    </div>
                    <div className="aw-kpi">
                        <span className="k">Conversions</span>
                        <b>37</b>
                    </div>
                    <div className="aw-kpi">
                        <span className="k">Top channel</span>
                        <b>Perplexity</b>
                    </div>
                </div>
                <div className="aw-card">
                    <div className="aw-card-h">Top earning pages</div>
                    <table className="aw-table">
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.pg}>
                                    <td className="pg">{r.pg}</td>
                                    <td className="src">{r.src}</td>
                                    <td className="amt">{r.amt}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

type PathOption = {
    id: SetupPath;
    logo: React.ReactNode;
    title: string;
    tag: string;
    sub: string;
    body: string;
    bullets: string[];
    preview: "ga4" | "thrivestack";
    comingSoon: boolean;
};

function PathStep({
    state,
    patch,
    requestOpen,
    onToggleRequest
}: {
    state: SetupState;
    patch: (p: Partial<SetupState>) => void;
    revenueStackAvailable: boolean;
    requestOpen: boolean;
    onToggleRequest: () => void;
}) {
    const c = COPY.pathChooser;
    const options: PathOption[] = [
        {
            id: "ga4",
            logo: <Ga4Logo />,
            title: c.ga4.title,
            tag: c.ga4.tag,
            sub: c.ga4.sub,
            body: c.ga4.body,
            bullets: c.ga4.bullets,
            preview: "ga4",
            comingSoon: false
        },
        {
            id: "revenue_stack",
            logo: <ThriveStackLogo />,
            title: c.revenueStack.title,
            tag: c.revenueStack.tag,
            sub: c.revenueStack.sub,
            body: c.revenueStack.body,
            bullets: c.revenueStack.bullets,
            preview: "thrivestack",
            comingSoon: true
        }
    ];

    // The right preview and the left detail follow the *committed* choice only — never
    // hover — defaulting to GA4 so the screen is never blank. (§ path chooser)
    const activeId = state.setupPath ?? "ga4";
    const active = options.find((o) => o.id === activeId) ?? options[0];

    return (
        <div className="rasx-step pathwide">
            <div className="rasx-pathx">
                {/* Left: heading, the two options, then what the chosen one connects. The
                    intro lives here (not full-width) so the right preview can rise to the top. */}
                <div className="rasx-pathx-left">
                    <StepIntro eyebrow={c.label} title={c.heading} lede={c.subheading} />
                    <div className="rasx-pathx-list" role="radiogroup" aria-label={c.heading}>
                        {options.map((opt) => {
                            const selected = state.setupPath === opt.id;
                            return (
                                <button
                                    key={opt.id}
                                    type="button"
                                    role="radio"
                                    aria-checked={selected}
                                    onClick={() => patch({ setupPath: opt.id, step: 1 })}
                                    className={`rasx-pathx-opt${selected ? " selected" : ""}${
                                        opt.comingSoon ? " soon" : ""
                                    }`}
                                >
                                    <span className="rasx-pathx-logo">{opt.logo}</span>
                                    <span className="rasx-pathx-opt-main">
                                        <span className="rasx-pathx-opt-name">{opt.title}</span>
                                        <span className="rasx-pathx-opt-sub">{opt.sub}</span>
                                    </span>
                                    {opt.comingSoon ? (
                                        <span className="rasx-tag soon">{opt.tag}</span>
                                    ) : (
                                        <span className="rasx-pathx-radio" aria-hidden="true">
                                            <Check strokeWidth={3} />
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    <div className="rasx-pathx-connect" key={activeId}>
                        <span className="rasx-pathx-connect-label">
                            {active.comingSoon ? c.detailLabelSoon : c.detailLabel}
                        </span>
                        <ul className="rasx-pathx-checks">
                            {active.bullets.map((b) => (
                                <li key={b}>
                                    <span className="ck">
                                        <Check strokeWidth={3} />
                                    </span>
                                    {b}
                                </li>
                            ))}
                        </ul>
                        {active.comingSoon && (
                            <button type="button" onClick={onToggleRequest} className="rasx-link">
                                {requestOpen ? c.hideRequest : c.requestFeature}
                            </button>
                        )}
                    </div>
                </div>

                {/* Right: just a peek at the product you get. */}
                <div className="rasx-pathx-right">
                    <div className="rasx-pathx-right-inner" key={activeId}>
                        {active.preview === "ga4" ? <Ga4Dashboard /> : <ThriveStackDashboard />}
                    </div>
                </div>
            </div>

            {requestOpen && (
                <div style={{ marginTop: 16 }}>
                    <RevenueStackComingSoon />
                </div>
            )}
        </div>
    );
}

// ── Connect GA4 ────────────────────────────────────────────────────────────────

function ConnectStep({
    state,
    patch,
    oauthError,
    exchanging,
    setupDetails,
    detailsLoading,
    onSelectProperty,
    onAuthoriseSync,
    authorising,
    airbyteAuthorised
}: {
    state: SetupState;
    patch: (p: Partial<SetupState>) => void;
    oauthError?: string | null;
    exchanging?: boolean;
    setupDetails?: Ga4SetupDetails | null;
    detailsLoading?: boolean;
    onSelectProperty?: (propertyId: string, forceRefresh?: boolean) => void;
    onAuthoriseSync?: () => void;
    authorising?: boolean;
    airbyteAuthorised?: boolean;
}) {
    const { isDemoMode } = useDemoMode();
    const c = COPY.wizard.step1;
    return (
        <div className="rasx-step">
            <StepIntro eyebrow={COPY.wizard.steps[0]} title={c.heading} lede={c.summary} />

            {exchanging && (
                <p className="rasx-note accent" style={{ marginBottom: 16 }}>
                    <Loader2 className="ic rasx-spin" size={15} />
                    {c.wizardConnecting}
                </p>
            )}

            {oauthError && !exchanging && (
                <div className="rasx-status warn" style={{ marginBottom: 16 }}>
                    <div className="rasx-status-head">
                        <AlertTriangle className="ic" size={16} strokeWidth={2} />
                        <span className="rasx-status-title">Connection didn&rsquo;t complete</span>
                    </div>
                    <p className="rasx-status-body">{oauthError}</p>
                </div>
            )}

            {isDemoMode ? (
                <MockGoogleOAuth
                    scopes={DEFAULT_OAUTH_SCOPES}
                    selectedPropertyIds={state.propertyIds}
                    connectedEmail={state.connected ? state.googleAccountEmail : null}
                    forcedError={state.devOauthError}
                    onSuccess={(account, propertyIds) =>
                        patch({ connected: true, googleAccountEmail: account.email, propertyIds })
                    }
                    onError={() => patch({ connected: false, propertyIds: [], googleAccountEmail: null })}
                    onDisconnect={() => patch({ connected: false, propertyIds: [], googleAccountEmail: null })}
                />
            ) : (
                <>
                    <Ga4Connect
                        connected={state.connected}
                        onDisconnect={() => patch({ connected: false, propertyIds: [], googleAccountEmail: null })}
                    />
                    {state.connected && state.propertyIds.length > 0 && (
                        <div className="rasx-note" style={{ marginTop: 16 }}>
                            {airbyteAuthorised ? (
                                <>
                                    <CheckCircle2 className="ic" size={16} style={{ color: "var(--done)" }} />
                                    <span>{c.syncAuthorised}</span>
                                </>
                            ) : (
                                <div>
                                    <p style={{ margin: 0 }}>{c.authoriseSyncBody}</p>
                                    <button
                                        type="button"
                                        onClick={() => onAuthoriseSync?.()}
                                        disabled={authorising}
                                        className="rasx-btn accent"
                                        style={{ marginTop: 12 }}
                                    >
                                        {authorising && <Loader2 className="spin" size={14} />}
                                        {authorising ? c.authorising : c.authoriseSync}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                    {state.connected && (
                        <Ga4LivePropertyPicker
                            accounts={setupDetails?.accounts ?? []}
                            selectedId={state.propertyIds[0] ?? null}
                            loading={Boolean(detailsLoading)}
                            onSelect={(property) => {
                                patch({ propertyIds: [property.propertyId] });
                                onSelectProperty?.(property.propertyId);
                            }}
                        />
                    )}
                </>
            )}
        </div>
    );
}

// ── AI channel check ─────────────────────────────────────────────────────────

/**
 * Copy for the channel step, reframed around "what we need → what we found → what
 * to do". Kept local to the v2 wizard so the legacy wizard's wording is untouched.
 */
const CHANNEL = {
    title: "Do you have an AI Assistant channel in GA4?",
    lede: "We check how this property files visits from ChatGPT, Perplexity, Gemini and other AI answer engines.",
    checking: "Checking your channel groups…",
    readError: "We couldn't read your channel groups from GA4 just now. Try again in a moment.",
    missingFound: "No AI Assistant channel found — these visits fall into **Referral** or **Direct**.",
    brokenFound:
        "AI channel found, but it matches **Default channel group** (not Source) and sits below Referral — so it reads zero.",
    okFound: "AI visits are filed into their own channel, above Referral.",
    fixToggleMissing: "How to add it in GA4",
    fixToggleBroken: "How to fix it in GA4",
    fixHint: "copy the regex, add one channel, order it above Referral",
    recheckMissing: "I've added it — re-check",
    recheckBroken: "I've fixed it — re-check",
    rechecking: "Re-checking…",
    skip: "Skip for now",
    openGa4: "Open GA4 channel groups",
    steps: [
        "GA4 → **Admin** → Data display → **Channel groups** → open your group → **Add channel**, name it `AI Search`.",
        "Set the condition to **Source** *matches regex* and paste the regex above. Add an OR rule: **Default channel group** *exactly matches* `AI Assistant`.",
        "Move it **above Referral**. GA4 stops at the first match, so below Referral the channel reads zero."
    ]
};

function ChannelStep({
    state,
    patch,
    setupDetails
}: {
    state: SetupState;
    patch: (p: Partial<SetupState>) => void;
    setupDetails?: Ga4SetupDetails | null;
}) {
    const { isDemoMode } = useDemoMode();
    const [checking, setChecking] = useState(false);
    const [rechecking, setRechecking] = useState(false);
    const [copied, setCopied] = useState(false);
    const [fixOpen, setFixOpen] = useState(false);

    // Live: the AI-channel diagnosis comes from the channel-groups API, evaluated
    // against AI_SOURCE_REGEX — every source must be present, order doesn't matter.
    const [groupsLoading, setGroupsLoading] = useState(false);
    const [groupsEval, setGroupsEval] = useState<AiChannelEvaluation | null>(null);
    const [groupsError, setGroupsError] = useState<string | null>(null);
    const propertyId = setupDetails?.propertyDetails?.propertyId || state.propertyIds[0] || "";

    const runChannelGroups = useCallback(
        async (forceRefresh: boolean) => {
            const refreshToken = readRefreshToken();
            if (!refreshToken || !propertyId) return;
            setGroupsLoading(true);
            setGroupsError(null);
            const res = await fetchGa4ChannelGroups({ refreshToken, propertyId, forceRefresh });
            setGroupsLoading(false);
            if (!res.ok) {
                setGroupsError(res.message);
                return;
            }
            const evaluation = evaluateAiChannel(res.data);
            setGroupsEval(evaluation);
            // Record the diagnosis so the Continue gate and a later revisit both see it.
            patch({ channelOutcome: evaluation.outcome });
        },
        // propertyId is the only external input; patch is stable.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [propertyId]
    );

    // Live diagnosis runs once the property is known; demo uses its mock below.
    useEffect(() => {
        if (isDemoMode || state.channelFixed || !propertyId) return;
        void runChannelGroups(false);
    }, [isDemoMode, state.channelFixed, propertyId, runChannelGroups]);

    const outcome: ChannelOutcome | null = state.channelFixed ? "OK" : state.channelOutcome;
    const loading = isDemoMode ? checking : groupsLoading;

    useEffect(() => {
        if (state.channelFixed || !isDemoMode) return;
        let cancelled = false;
        setChecking(true);
        mockDelay(1200).then(() => {
            if (cancelled) return;
            setChecking(false);
            patch({ channelOutcome: state.devChannelOutcome });
        });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.devChannelOutcome, state.channelFixed]);

    // No auto-advance: even when the channel is correct the step waits, so the user can
    // see which AI Assistant channel(s) we matched and confirm before continuing (§3).

    // The AI sources we credit, and — once diagnosed live — which of them the matched
    // channel already covers. Demo has no live evaluation, so it shows the full set as
    // covered on an OK outcome.
    const requiredSources = useMemo(
        () =>
            Array.from(
                new Set(
                    AI_SOURCE_REGEX.split("|")
                        .map((s) => s.replace(/\\/g, "").trim().toLowerCase())
                        .filter(Boolean)
                )
            ),
        []
    );
    const coveredSources = groupsEval
        ? requiredSources.filter((s) => !groupsEval.missing.includes(s))
        : requiredSources;

    const copyRegex = async () => {
        try {
            await navigator.clipboard.writeText(AI_SOURCE_REGEX);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch {
            /* clipboard blocked — the regex is selectable */
        }
    };

    const recheck = async () => {
        // Live: re-read the channel groups from GA4 (bypassing the cache). Demo:
        // simulate the fix landing so the flow can be walked end to end.
        if (!isDemoMode) {
            setRechecking(true);
            await runChannelGroups(true);
            setRechecking(false);
            return;
        }
        setRechecking(true);
        await mockDelay(1600);
        setRechecking(false);
        patch({ channelFixed: true, channelOutcome: "OK", channelSkipped: false });
    };

    const isChecking = loading || (outcome === null && !groupsError);
    const isProblem = !isChecking && (outcome === "MISSING" || outcome === "BROKEN");

    // One line, leading with what GA4 actually has — the older need/found pair is gone.
    const resultLine = (() => {
        if (isChecking) {
            return (
                <CheckResult tone="none" loading>
                    <span className="rasx-muted">{CHANNEL.checking}</span>
                </CheckResult>
            );
        }
        if (groupsError) {
            return (
                <CheckResult tone="warn" pill="Try again">
                    {CHANNEL.readError}
                </CheckResult>
            );
        }
        if (outcome === "OK") {
            return (
                <CheckResult tone="ok" pill="Found">
                    <RichText text={CHANNEL.okFound} />
                </CheckResult>
            );
        }
        if (outcome === "BROKEN") {
            return (
                <CheckResult tone="warn" pill="Needs a fix">
                    <RichText text={CHANNEL.brokenFound} />
                </CheckResult>
            );
        }
        return (
            <CheckResult tone="none" pill="Not found">
                <RichText text={CHANNEL.missingFound} />
            </CheckResult>
        );
    })();

    return (
        <div className="rasx-step">
            <StepIntro eyebrow={COPY.wizard.steps[1]} title={CHANNEL.title} lede={CHANNEL.lede} />

            {resultLine}

            {/* What we matched — shown once the check settles, so a correct channel WAITS
                here (no auto-advance) and the user can see the AI Assistant channel(s) and
                the sources filed into them before continuing (§3). */}
            {!isChecking && !groupsError && (outcome === "OK" || (outcome === "BROKEN" && groupsEval)) && (
                <div className="rasx-found">
                    {groupsEval?.matchedChannel && (
                        <div className="rasx-found-name">
                            <span className="rasx-pair-k">AI Assistant channel</span>
                            <span className="rasx-found-chip strong">{groupsEval.matchedChannel}</span>
                        </div>
                    )}
                    <div>
                        <span className="rasx-pair-k">
                            {outcome === "OK" ? "AI sources filed here" : "AI sources covered"}
                        </span>
                        <div className="rasx-found-chips">
                            {coveredSources.map((s) => (
                                <span key={s} className="rasx-found-chip">
                                    {s}
                                </span>
                            ))}
                        </div>
                    </div>
                    {outcome === "BROKEN" && groupsEval && groupsEval.missing.length > 0 && (
                        <div>
                            <span className="rasx-pair-k warn">Not yet covered</span>
                            <div className="rasx-found-chips">
                                {groupsEval.missing.map((s) => (
                                    <span key={s} className="rasx-found-chip missing">
                                        {s}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {isProblem && (
                <>
                    {/* Fix instructions first — people read how to fix, then act on it. */}
                    <div className={`rasx-disclosure${fixOpen ? " open" : ""}`}>
                        <button
                            type="button"
                            className="rasx-disclosure-head"
                            aria-expanded={fixOpen}
                            onClick={() => setFixOpen((v) => !v)}
                        >
                            <span className="lead">
                                <b>{outcome === "MISSING" ? CHANNEL.fixToggleMissing : CHANNEL.fixToggleBroken}</b>
                                <span className="rasx-muted"> — {CHANNEL.fixHint}</span>
                            </span>
                            <ChevronDown className="chev" size={16} strokeWidth={2} />
                        </button>
                        {fixOpen && (
                            <div className="rasx-disclosure-body">
                                <div className="rasx-code">
                                    <button
                                        type="button"
                                        onClick={copyRegex}
                                        className="rasx-code-copy"
                                        aria-label="Copy regex"
                                    >
                                        {copied ? (
                                            <Check size={12} strokeWidth={2.5} style={{ color: "var(--done)" }} />
                                        ) : (
                                            <Copy size={12} strokeWidth={2} />
                                        )}
                                        {copied ? "Copied" : "Copy"}
                                    </button>
                                    <pre className="rasx-code-pre">
                                        <code>{AI_SOURCE_REGEX}</code>
                                    </pre>
                                </div>
                                <ol className="rasx-steps">
                                    {CHANNEL.steps.map((s) => (
                                        <li key={s}>
                                            <RichText text={s} />
                                        </li>
                                    ))}
                                </ol>
                            </div>
                        )}
                    </div>

                    {/* Action follows the how-to: fix it in GA4, then re-check. No skip — the
                        channel has to be resolved before attribution can trust the split. */}
                    <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                        <button type="button" onClick={recheck} disabled={rechecking} className="rasx-btn dark">
                            {rechecking && <Loader2 className="spin" size={14} />}
                            {rechecking
                                ? CHANNEL.rechecking
                                : outcome === "MISSING"
                                ? CHANNEL.recheckMissing
                                : CHANNEL.recheckBroken}
                        </button>
                        <GaAdminLink label={CHANNEL.openGa4} />
                        <DocsLink />
                    </div>
                </>
            )}

            {/* Read failed (not a diagnosis) — offer a retry rather than a dead end. */}
            {groupsError && !isProblem && (
                <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                    <button type="button" onClick={recheck} disabled={rechecking} className="rasx-btn dark">
                        {rechecking && <Loader2 className="spin" size={14} />}
                        {rechecking ? CHANNEL.rechecking : "Try again"}
                    </button>
                    <GaAdminLink label={CHANNEL.openGa4} />
                </div>
            )}
        </div>
    );
}

// ── Conversion events & value ───────────────────────────────────────────────────

/**
 * Combined events + value step. GA4 does the work — we look for conversions that
 * already carry revenue and only ask for what's missing. Three states, chosen for
 * the user off what GA4 returns (they never pick a mode):
 *
 *   found      — revenue is reported → review the events, confirm, done
 *   noRevenue  — key events, no value → add revenue in GA4, or estimate here
 *   noEvents   — no key events → mark one in GA4, then re-scan
 *
 * Effort rises only as GA4 data falls (confirm → fill → go fix in GA4), and every
 * state ends in the same place: a confirmed set of value-bearing conversions.
 */
const REVENUE = {
    lede: "GA4 does the work — we look for conversions that already report revenue, and only ask for what's missing.",
    // One question across every state — the result line below carries the outcome.
    title: "Which events count as a conversion?",

    // found — GA4 already reports revenue
    foundRevenue: "GA4 already reports revenue on your conversions.",
    revenueBadge: "revenue tracked",
    reviewHint:
        "Select the events that count as a conversion. The revenue shown for each is the total GA4 revenue aggregated across the window, with its event count.",

    // noRevenue — key events exist, but no value
    foundNoRevenue: "Key events found, but GA4 isn't reporting a revenue value on them yet.",
    routeIntro: "Two ways forward — pick whichever is easier:",
    recheck: "I've added it in GA4 — re-check",
    rechecking: "Re-checking…",
    estimateCta: "Estimate values here instead",
    estimateHelper: COPY.wizard.step4.helper,

    // noEvents — nothing marked as a key event
    foundNoEvents: "No key events marked in GA4 yet.",
    fixNote: "Mark at least one event as a key event in GA4 → **Admin** → **Events**. Two clicks, no code.",
    rescan: "I've marked one — re-scan",
    rescanning: "Re-scanning…",

    // shared
    openGa4: "Open GA4 events",
    searchPlaceholder: "Search events",
    noMatch: "No events match your search.",
    revenueColLabel: "Aggregated GA4 revenue · 30d",
    atLeastOne: "Select at least one event to continue.",
    loadProperty: "Select a property to load its key events.",
    selectedSummary: (n: number, total: number) => `${n} of ${total} selected`
};

type RevenuePhase = "found" | "noRevenue" | "noEvents";

function RevenueEventsStep({
    state,
    patch,
    setupDetails,
    detailsLoading = false,
    onSelectProperty
}: {
    state: SetupState;
    patch: (p: Partial<SetupState>) => void;
    setupDetails?: Ga4SetupDetails | null;
    detailsLoading?: boolean;
    onSelectProperty?: (propertyId: string, forceRefresh?: boolean) => void;
}) {
    const { isDemoMode } = useDemoMode();
    const liveProbe = setupDetails?.keyEvents ?? null;
    const liveRevenue = setupDetails?.revenue ?? null;
    // Per-event revenue: demo carries it on KEY_EVENTS; live now gets it from the
    // revenue probe's `conversionEvents`, each declaring where its worth comes from
    // (GA4_NATIVE / EVENT_VALUE / NONE) so we only ask for what GA4 doesn't have.
    type EvRow = {
        name: string;
        count: number;
        /** Known GA4 revenue for the event (GA4_NATIVE), else null. */
        revenue: number | null;
        /** Live only — GA4's classification of where the event's worth came from. */
        source?: Ga4RevenueSource;
        /** Live only — true when the tenant must state a value (revenueSource NONE). */
        needsValue?: boolean;
        /** Live only — the "value" parameter GA4 carries for EVENT_VALUE events. */
        eventValue?: number;
    };
    const rows: EvRow[] = isDemoMode
        ? KEY_EVENTS.map((e) => ({ name: e.name, count: e.count90d, revenue: e.revenue90d }))
        : (liveRevenue?.conversionEvents ?? []).length > 0
        ? (liveRevenue?.conversionEvents ?? []).map((e) => ({
              name: e.name,
              count: e.keyEvents || e.eventCount,
              revenue: e.revenueSource === "GA4_NATIVE" ? e.totalRevenue : null,
              source: e.revenueSource,
              needsValue: e.needsStatedValue,
              eventValue: e.eventValue
          }))
        : // Fallback: the revenue probe returned no conversion events but the
          // key-events probe still has the list (revenue unknown per event).
          (liveProbe?.keyEvents ?? []).map((e) => ({
              name: e.name,
              count: e.keyEventCount || e.count,
              revenue: null,
              needsValue: true
          }));

    // Live: no key events marked at all — driven by either probe.
    const noKeyEvents = isDemoMode
        ? state.devNoKeyEvents
        : liveRevenue != null
        ? !liveRevenue.hasKeyEvents
        : liveProbe !== null && !liveProbe.hasAnyKeyEvents;
    // Live: some conversion needs a stated value (revenueSource NONE) → the
    // "add a value" route; otherwise every event already carries its worth.
    const liveNeedsValue = rows.some((r) => r.needsValue);
    const hasGa4Revenue = isDemoMode ? state.devGa4Revenue : !liveNeedsValue;
    const phase: RevenuePhase = noKeyEvents ? "noEvents" : hasGa4Revenue ? "found" : "noRevenue";

    // Persist the resolved revenue path so the backend picks native vs derived correctly (it
    // can't derive this from the blob otherwise). Skip the no-events phase, where it's undefined.
    useEffect(() => {
        if (phase === "noEvents") return;
        if (state.hasGa4Revenue !== hasGa4Revenue) patch({ hasGa4Revenue });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phase, hasGa4Revenue]);

    // Re-check (revenue) and re-scan (key events) both point at GA4; in demo they
    // simulate the fix landing so the flow can be walked end to end.
    const [busy, setBusy] = useState<null | "events" | "revenue">(null);
    // The estimate inputs stay behind a button — fixing it in GA4 is the primary route.
    const [showEstimate, setShowEstimate] = useState(false);
    const [query, setQuery] = useState("");
    const symbol = currencySymbol(state.currency);
    const period = "30";
    // Demo keeps the estimate behind a button; live GA4 now hands us per-event
    // classification, so events that need a value show their input inline.
    const estimating = isDemoMode ? phase === "noRevenue" && showEstimate : phase === "noRevenue";

    // Live: the setup-details probe carries this step's key events and revenue. It is held in
    // component state on the page, so it is lost across the Airbyte consent redirect (and never
    // runs at all if the step is reached without re-selecting the property). Re-run it here for
    // the selected property so the events + revenue actually load, rather than showing an empty
    // step with no request in flight (§5).
    const probePid = state.propertyIds[0] || "";
    const requestedProbeFor = useRef<string | null>(null);
    useEffect(() => {
        if (isDemoMode || !probePid) return;
        const covered =
            liveRevenue?.propertyId === probePid ||
            (setupDetails?.propertyDetails?.propertyId === probePid &&
                (setupDetails?.revenue != null || setupDetails?.keyEvents != null));
        if (covered || detailsLoading) {
            if (covered) requestedProbeFor.current = probePid;
            return;
        }
        if (requestedProbeFor.current === probePid) return;
        requestedProbeFor.current = probePid;
        onSelectProperty?.(probePid, false);
    }, [isDemoMode, probePid, liveRevenue, setupDetails, detailsLoading, onSelectProperty]);

    // Live: adopt GA4's configured currency for stated values — never assume USD.
    const probeCurrency = liveRevenue?.currency || setupDetails?.propertyDetails?.currencyCode || "";
    useEffect(() => {
        // Seed once per probe currency; a later manual change is not overridden
        // because the effect only re-runs when the probe currency itself changes.
        if (!isDemoMode && probeCurrency && probeCurrency !== state.currency) {
            patch({ currency: probeCurrency });
        }
    }, [isDemoMode, probeCurrency]);

    // The event list can be long, so it's searchable and scrolls at a fixed height.
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return q ? rows.filter((e) => e.name.toLowerCase().includes(q)) : rows;
    }, [rows, query]);

    const toggle = (name: string) => {
        const next = state.selectedEvents.includes(name)
            ? state.selectedEvents.filter((e) => e !== name)
            : [...state.selectedEvents, name];
        patch({ selectedEvents: next });
    };

    // Reconcile the persisted selection with what GA4 actually reports now. A property
    // or probe change can leave selected events that no longer exist in the list, which
    // otherwise surfaces as an impossible "5 of 3 selected". Prune once rows are known.
    const rowNamesKey = rows.map((r) => r.name).join("");
    useEffect(() => {
        if (rows.length === 0) return;
        const present = new Set(rows.map((r) => r.name));
        const pruned = state.selectedEvents.filter((n) => present.has(n));
        if (pruned.length !== state.selectedEvents.length) patch({ selectedEvents: pruned });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rowNamesKey]);

    // The property whose probes are on screen — prefer what the probe reported,
    // falling back to the picked id. Re-scan/re-check re-run every probe for it.
    const selectedPropertyId =
        setupDetails?.propertyDetails?.propertyId || liveRevenue?.propertyId || state.propertyIds[0] || "";
    // Live re-probe in flight, surfaced on whichever button triggered it.
    const reprobing = !isDemoMode && detailsLoading;

    const rescan = async () => {
        if (!isDemoMode) {
            if (selectedPropertyId) onSelectProperty?.(selectedPropertyId, true);
            return;
        }
        setBusy("events");
        await mockDelay(1400);
        setBusy(null);
        patch({ devNoKeyEvents: false });
    };
    const recheckRevenue = async () => {
        if (!isDemoMode) {
            // GA4 now reports per-event worth, so a re-check re-runs the probe and
            // the events flip from "needs a value" to their real revenue on their own.
            if (selectedPropertyId) onSelectProperty?.(selectedPropertyId, true);
            return;
        }
        setBusy("revenue");
        await mockDelay(1400);
        setBusy(null);
        patch({ devGa4Revenue: true });
    };

    // One line, leading with what GA4 has — replaces the old need/found pair.
    const resultLine =
        phase === "found" ? (
            <CheckResult tone="ok" pill="Found">
                {REVENUE.foundRevenue}
            </CheckResult>
        ) : phase === "noEvents" ? (
            <CheckResult tone="none" pill="Not found">
                {REVENUE.foundNoEvents}
            </CheckResult>
        ) : (
            <CheckResult tone="warn" pill="Add a value">
                {REVENUE.foundNoRevenue}
            </CheckResult>
        );

    // A currency input for a stated deal value — shown for the events GA4 has no
    // worth for, and only once selected.
    const valueInput = (e: EvRow, selected: boolean) =>
        selected ? (
            <span className="rasx-money">
                <span className="sym">{symbol}</span>
                <input
                    type="number"
                    min={0}
                    value={state.eventValues[e.name] ?? 0}
                    onChange={(ev) =>
                        patch({ eventValues: { ...state.eventValues, [e.name]: Number(ev.target.value) } })
                    }
                    className="rasx-input"
                />
            </span>
        ) : (
            <span className="rasx-evt-dash">—</span>
        );

    const revenueText = (amount: number) => (
        <span className="rasx-evt-rev" title={`Total GA4 revenue aggregated over the last ${period} days`}>
            {symbol}
            {amount.toLocaleString()}
        </span>
    );
    const revenueBadge = (
        <span className="rasx-rev-badge">
            <CheckCircle2 size={12} strokeWidth={2.5} />
            {REVENUE.revenueBadge}
        </span>
    );

    // Middle column of each row. Demo resolves off the coarse phase; live resolves
    // per event off GA4's revenue source, so a property with a mix of native and
    // custom conversions only prompts for the custom ones.
    const renderValueCell = (e: EvRow, selected: boolean) => {
        if (!isDemoMode) {
            if (e.needsValue) return valueInput(e, selected);
            // EVENT_VALUE carries a known "value" parameter — show it, no ask.
            if (e.source === "EVENT_VALUE") return revenueText(e.eventValue ?? 0);
            // GA4_NATIVE — real reported revenue, or the tracked badge if zero so far.
            return e.revenue != null && e.revenue > 0 ? revenueText(e.revenue) : revenueBadge;
        }
        if (estimating) return valueInput(e, selected);
        if (phase === "found") return e.revenue != null ? revenueText(e.revenue) : revenueBadge;
        return null;
    };

    // Searchable, fixed-height, scrolling event table — shared by the found and
    // no-revenue phases (the no-events phase has no events to show).
    const eventsTable = (
        <div className="rasx-evt" style={{ marginTop: 16 }}>
            <div className="rasx-evt-head">
                <span className="rasx-evt-search">
                    <Search size={14} strokeWidth={2} />
                    <input
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={REVENUE.searchPlaceholder}
                    />
                </span>
                {estimating && (
                    <span className="rasx-evt-cur">
                        <span>Currency</span>
                        <select
                            value={state.currency}
                            onChange={(e) => patch({ currency: e.target.value })}
                            className="rasx-select"
                            style={{ width: 88 }}
                        >
                            {CURRENCIES.map((cur) => (
                                <option key={cur.code} value={cur.code}>
                                    {cur.code}
                                </option>
                            ))}
                        </select>
                    </span>
                )}
            </div>

            <div className="rasx-evt-scroll">
                {filtered.length === 0 ? (
                    <p className="rasx-evt-empty">{rows.length === 0 ? REVENUE.loadProperty : REVENUE.noMatch}</p>
                ) : (
                    filtered.map((e) => {
                        const selected = state.selectedEvents.includes(e.name);
                        return (
                            <div key={e.name} className={`rasx-evt-row${selected ? " on" : ""}`}>
                                <input type="checkbox" checked={selected} onChange={() => toggle(e.name)} />
                                <span className="rasx-evt-name" onClick={() => toggle(e.name)}>
                                    {e.name}
                                </span>
                                {renderValueCell(e, selected)}
                                <span className="rasx-evt-count">
                                    {e.count.toLocaleString()} · {period}d
                                </span>
                            </div>
                        );
                    })
                )}
            </div>

            <div className="rasx-evt-foot">
                <span>{REVENUE.selectedSummary(state.selectedEvents.length, rows.length)}</span>
                {phase === "found" && (
                    <span className="rasx-evt-foottag">
                        <CheckCircle2 size={12} strokeWidth={2.5} />
                        {REVENUE.revenueColLabel}
                    </span>
                )}
            </div>
        </div>
    );

    return (
        <div className="rasx-step">
            <StepIntro eyebrow={COPY.wizard.steps[2]} title={REVENUE.title} lede={REVENUE.lede} />

            {resultLine}

            {phase === "noEvents" && (
                <>
                    <p className="rasx-note" style={{ marginTop: 16 }}>
                        <Info className="ic" size={15} />
                        <span>
                            <RichText text={REVENUE.fixNote} />
                        </span>
                    </p>
                    <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                        <button
                            type="button"
                            onClick={rescan}
                            disabled={busy !== null || reprobing}
                            className="rasx-btn dark"
                        >
                            {(busy === "events" || reprobing) && <Loader2 className="spin" size={14} />}
                            {busy === "events" || reprobing ? REVENUE.rescanning : REVENUE.rescan}
                        </button>
                        <GaAdminLink label={REVENUE.openGa4} />
                        <DocsLink />
                    </div>
                </>
            )}

            {phase === "found" && (
                <>
                    <p className="rasx-note" style={{ marginTop: 16 }}>
                        <Info className="ic" size={15} />
                        <span>{REVENUE.reviewHint}</span>
                    </p>
                    {eventsTable}
                    {rows.length > 0 && state.selectedEvents.length === 0 && (
                        <p className="rasx-inline-note">{REVENUE.atLeastOne}</p>
                    )}
                </>
            )}

            {phase === "noRevenue" && (
                <>
                    {eventsTable}
                    {rows.length > 0 && state.selectedEvents.length === 0 && (
                        <p className="rasx-inline-note">{REVENUE.atLeastOne}</p>
                    )}
                    <p className="rasx-note" style={{ marginTop: 16 }}>
                        <Info className="ic" size={15} />
                        <span>{estimating ? REVENUE.estimateHelper : REVENUE.routeIntro}</span>
                    </p>
                    {/* Fixing it in GA4 is the primary route; the estimate reveals inline in the table. */}
                    <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                        <button
                            type="button"
                            onClick={recheckRevenue}
                            disabled={busy !== null || reprobing}
                            className="rasx-btn dark"
                        >
                            {(busy === "revenue" || reprobing) && <Loader2 className="spin" size={14} />}
                            {busy === "revenue" || reprobing ? REVENUE.rechecking : REVENUE.recheck}
                        </button>
                        {/* Manual estimate is GA4-only for now — hidden in live mode,
                            kept behind demo so the fallback flow can still be walked
                            and re-enabled later. */}
                        {isDemoMode && (
                            <button
                                type="button"
                                onClick={() => setShowEstimate((v) => !v)}
                                className={`rasx-btn${showEstimate ? " active" : ""}`}
                            >
                                {REVENUE.estimateCta}
                            </button>
                        )}
                        <GaAdminLink label={REVENUE.openGa4} />
                        <DocsLink />
                    </div>
                </>
            )}
        </div>
    );
}

// ── What happens next ──────────────────────────────────────────────────────────

/** Local copy for the two-phase finish: create connection, then the (long) first sync. */
const NEXT = {
    createTitle: "Creating the connection…",
    createBody: "This usually takes 30 seconds to 2 minutes. You can wait here.",
    connectionCreated: "Connection created",
    syncRunning: "First sync running — this can take a while.",
    leaveBody: "You can close this window. We'll email you when your first report is ready.",
    notifyLabel: "Who we'll email when it's ready",
    add: "Add email",
    addPlaceholder: "name@company.com",
    invalidEmail: "Enter a valid email address.",
    dupeEmail: "That email is already on the list.",
    emptyEmails: "Add at least one email so we can let you know.",
    createCta: "Create connection"
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function WhatsNextStep({
    state,
    defaultEmail,
    onStartSync,
    syncing,
    syncStartedAt,
    connectionExisted
}: {
    state: SetupState;
    /** Pre-filled recipient — the connected Google account in demo, the logged-in user live (§7). */
    defaultEmail?: string | null;
    onStartSync?: () => void;
    syncing?: boolean;
    syncStartedAt?: string | null;
    connectionExisted?: boolean;
}) {
    const c = COPY.whatsNext;
    const started = Boolean(syncStartedAt);

    // Notification recipients. Defaults to the logged-in user (or the connected Google account
    // in demo); editable so a team can add whoever should hear that the report is ready.
    // (Demo/local only — not yet persisted to the backend.)
    const initialRecipient = defaultEmail || state.googleAccountEmail;
    const [emails, setEmails] = useState<string[]>(() => (initialRecipient ? [initialRecipient] : []));
    const [newEmail, setNewEmail] = useState("");
    const [emailError, setEmailError] = useState<string | null>(null);
    const [adding, setAdding] = useState(false);

    const addEmail = () => {
        const v = newEmail.trim();
        if (!EMAIL_RE.test(v)) {
            setEmailError(NEXT.invalidEmail);
            return;
        }
        if (emails.includes(v)) {
            setEmailError(NEXT.dupeEmail);
            return;
        }
        setEmails((prev) => [...prev, v]);
        setNewEmail("");
        setEmailError(null);
        // Input stays open so several can be added in a row.
    };

    // Compact tag input: chips inline with an "add" affordance that only becomes a
    // field when asked for, so the recipient list stays small.
    const emailRecipients = (
        <div style={{ marginTop: 18 }}>
            <span className="rasx-field-label" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <Mail size={13} style={{ color: "var(--ink-3)" }} />
                {NEXT.notifyLabel}
            </span>
            <div className="rasx-emails">
                {emails.map((e) => (
                    <span key={e} className="rasx-email-chip">
                        {e}
                        <button
                            type="button"
                            aria-label={`Remove ${e}`}
                            onClick={() => setEmails((prev) => prev.filter((x) => x !== e))}
                        >
                            <X size={12} strokeWidth={2} />
                        </button>
                    </span>
                ))}
                {adding ? (
                    <input
                        className="rasx-email-input"
                        type="email"
                        autoFocus
                        placeholder={NEXT.addPlaceholder}
                        value={newEmail}
                        onChange={(e) => {
                            setNewEmail(e.target.value);
                            setEmailError(null);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                addEmail();
                            } else if (e.key === "Escape") {
                                setNewEmail("");
                                setEmailError(null);
                                setAdding(false);
                            }
                        }}
                        onBlur={() => {
                            if (!newEmail.trim()) {
                                setEmailError(null);
                                setAdding(false);
                            }
                        }}
                    />
                ) : (
                    <button type="button" className="rasx-email-add-btn" onClick={() => setAdding(true)}>
                        <Plus strokeWidth={2} /> {NEXT.add}
                    </button>
                )}
            </div>
            {emailError && <p className="rasx-inline-note">{emailError}</p>}
        </div>
    );

    return (
        <div className="rasx-step">
            <StepIntro eyebrow={COPY.wizard.steps[4]} title={c.heading} lede={c.summary} />
            <ul className="rasx-timeline">
                {c.steps.map((step, i) => (
                    <li key={step.title}>
                        <span className="n">{i + 1}</span>
                        <div>
                            <div className="tt">{step.title}</div>
                            <div className="tb">{step.body}</div>
                        </div>
                    </li>
                ))}
            </ul>

            {started ? (
                // Phase 2 — connection made, first sync running (can be long).
                <>
                    <div className="rasx-pair" style={{ marginTop: 22 }}>
                        <div className="rasx-pair-row ok">
                            <CheckCircle2 className="ic" size={16} strokeWidth={2} />
                            <div className="rasx-pair-v" style={{ marginTop: 0, fontWeight: 600 }}>
                                {NEXT.connectionCreated}
                            </div>
                        </div>
                        <div className="rasx-pair-row">
                            <Loader2 className="ic rasx-spin" size={16} />
                            <div className="rasx-pair-v" style={{ marginTop: 0 }}>
                                {NEXT.syncRunning}
                            </div>
                        </div>
                    </div>
                    <p className="rasx-note" style={{ marginTop: 14 }}>
                        <Mail className="ic" size={15} />
                        <span>{NEXT.leaveBody}</span>
                    </p>
                    {emailRecipients}
                    {connectionExisted && (
                        <p className="rasx-muted" style={{ marginTop: 12, fontSize: 11.5 }}>
                            {c.alreadyConnected}
                        </p>
                    )}
                </>
            ) : syncing ? (
                // Phase 1 — creating the connection.
                <div className="rasx-status" style={{ marginTop: 22, background: "var(--field)" }}>
                    <div className="rasx-status-head">
                        <Loader2 className="ic rasx-spin" size={16} />
                        <span className="rasx-status-title">{NEXT.createTitle}</span>
                    </div>
                    <p className="rasx-status-body">{NEXT.createBody}</p>
                </div>
            ) : (
                // Phase 0 — idle: set recipients, then create the connection.
                <>
                    <div className="rasx-note" style={{ marginTop: 22, flexDirection: "column", gap: 6 }}>
                        <span style={{ fontWeight: 600, color: "var(--ink)" }}>{c.noteTitle}</span>
                        <span>{c.noteBody}</span>
                        <span style={{ fontWeight: 550, color: "var(--ink)" }}>{c.noteClosing}</span>
                    </div>
                    {emailRecipients}
                    <div style={{ marginTop: 20 }}>
                        <button
                            type="button"
                            onClick={() => onStartSync?.()}
                            disabled={state.propertyIds.length === 0}
                            className="rasx-btn accent"
                        >
                            {NEXT.createCta}
                        </button>
                        <p className="rasx-muted" style={{ marginTop: 8, fontSize: 11.5, lineHeight: 1.5 }}>
                            {c.secondConsent}
                        </p>
                    </div>
                </>
            )}
        </div>
    );
}

// ── Demo controls (deliberately separated from the product UI) ──────────────────

function DemoControls({
    currentId,
    state,
    patch
}: {
    currentId: StepId | undefined;
    state: SetupState;
    patch: (p: Partial<SetupState>) => void;
}) {
    let chips: React.ReactNode = null;

    if (currentId === "connect") {
        chips = (
            <>
                <button
                    type="button"
                    className={`rasx-demochip${state.devOauthError === null ? " active" : ""}`}
                    onClick={() => patch({ devOauthError: null })}
                >
                    Happy path
                </button>
                {OAUTH_ERROR_CODES.map((code) => (
                    <button
                        key={code}
                        type="button"
                        className={`rasx-demochip${state.devOauthError === code ? " active" : ""}`}
                        onClick={() => patch({ devOauthError: code })}
                    >
                        {code}
                    </button>
                ))}
            </>
        );
    } else if (currentId === "channel") {
        chips = (["MISSING", "BROKEN", "OK"] as ChannelOutcome[]).map((o) => (
            <button
                key={o}
                type="button"
                className={`rasx-demochip${state.devChannelOutcome === o ? " active" : ""}`}
                onClick={() =>
                    patch({
                        devChannelOutcome: o,
                        channelFixed: false,
                        channelOutcome: null,
                        channelAutoAdvanced: false
                    })
                }
            >
                {o}
            </button>
        ));
    } else if (currentId === "events") {
        // One step, three GA4 outcomes — the same fork the step renders off.
        chips = (
            <>
                <button
                    type="button"
                    className={`rasx-demochip${!state.devNoKeyEvents && state.devGa4Revenue ? " active" : ""}`}
                    onClick={() => patch({ devNoKeyEvents: false, devGa4Revenue: true })}
                >
                    Revenue found
                </button>
                <button
                    type="button"
                    className={`rasx-demochip${!state.devNoKeyEvents && !state.devGa4Revenue ? " active" : ""}`}
                    onClick={() => patch({ devNoKeyEvents: false, devGa4Revenue: false })}
                >
                    Key events, no revenue
                </button>
                <button
                    type="button"
                    className={`rasx-demochip${state.devNoKeyEvents ? " active" : ""}`}
                    onClick={() => patch({ devNoKeyEvents: true })}
                >
                    No key events
                </button>
            </>
        );
    }

    if (!chips) return null;

    return (
        <div className="rasx-demobar">
            <span className="rasx-demobar-label">Demo</span>
            {chips}
        </div>
    );
}

// ── Events-step completeness ─────────────────────────────────────────────────

/**
 * Which of the selected key events still need a stated deal value before the events step can
 * continue (§8). Demo: the "key events, no revenue" phase needs a value for every selected
 * event; the "revenue found" phase needs none. Live: only events GA4 reports no worth for
 * (revenueSource NONE → needsStatedValue) need one — native/EVENT_VALUE events already carry it.
 */
function selectedEventsNeedingValue(
    state: SetupState,
    setupDetails: Ga4SetupDetails | null | undefined,
    isDemoMode: boolean
): string[] {
    if (isDemoMode) {
        const noRevenuePhase = !state.devNoKeyEvents && !state.devGa4Revenue;
        return noRevenuePhase ? [...state.selectedEvents] : [];
    }
    const byName = new Map((setupDetails?.revenue?.conversionEvents ?? []).map((e) => [e.name, e]));
    const keyNames = new Set((setupDetails?.keyEvents?.keyEvents ?? []).map((e) => e.name));
    return state.selectedEvents.filter((name) => {
        const c = byName.get(name);
        if (c) return c.needsStatedValue;
        // No per-event classification, but it is a marked key event: worth unknown, so a value
        // is required (mirrors the events table's needsValue fallback).
        return keyNames.has(name);
    });
}

// ── Shell ──────────────────────────────────────────────────────────────────────

export function RevenueAttributionSetupWizardV2({
    state,
    patch,
    onClose,
    onFinish,
    oauthError = null,
    exchanging = false,
    onDismissOauthError,
    setupDetails = null,
    detailsLoading = false,
    onSelectProperty,
    onAuthoriseSync,
    authorising = false,
    airbyteAuthorised = false,
    secretExpired = false,
    onStartSync,
    syncing = false,
    syncStartedAt = null,
    connectionExisted = false,
    defaultNotifyEmail = null
}: {
    state: SetupState;
    patch: (p: Partial<SetupState>) => void;
    onClose: () => void;
    onFinish: () => void;
    /** Default report-ready recipient — logged-in user in live, connected account in demo (§7). */
    defaultNotifyEmail?: string | null;
    oauthError?: string | null;
    exchanging?: boolean;
    onDismissOauthError?: () => void;
    setupDetails?: Ga4SetupDetails | null;
    detailsLoading?: boolean;
    onSelectProperty?: (propertyId: string, forceRefresh?: boolean) => void;
    onAuthoriseSync?: () => void;
    authorising?: boolean;
    airbyteAuthorised?: boolean;
    secretExpired?: boolean;
    onStartSync?: () => void;
    syncing?: boolean;
    syncStartedAt?: string | null;
    connectionExisted?: boolean;
}) {
    const { isDemoMode } = useDemoMode();
    const [requestOpen, setRequestOpen] = useState(false);
    const rows = stepRowsFor(state.setupPath, isDemoMode);
    const currentStep = Math.min(Math.max(state.step, 1), rows.length);
    const currentId = rows[currentStep - 1]?.id;
    const isLastStep = currentStep === rows.length;

    const goTo = useCallback((step: number) => patch({ step }), [patch]);
    // §4 — record the step we're leaving as completed, so progress survives a reload (the page
    // persists the blob when completedSteps grows) rather than only landing at finishSetup.
    const withStepCompleted = useCallback(
        (extra: Partial<SetupState>) => {
            const completedSteps =
                currentId && !state.completedSteps.includes(currentId)
                    ? [...state.completedSteps, currentId]
                    : state.completedSteps;
            patch({ ...extra, completedSteps });
        },
        [currentId, state.completedSteps, patch]
    );
    const next = useCallback(
        () => withStepCompleted({ step: Math.min(currentStep + 1, rows.length) }),
        [withStepCompleted, currentStep, rows.length]
    );
    const handleFinish = useCallback(() => {
        withStepCompleted({});
        onFinish();
    }, [withStepCompleted, onFinish]);
    const back = () => goTo(Math.max(currentStep - 1, 1));

    // The stepper shows the numbered steps only (path chooser and the live
    // coming-soon page are decision screens without a progress rail).
    const showStepper = currentId !== "path" && currentId !== "comingSoon" && rows.length > 2;
    const subRows = rows.slice(1);
    const activeSubIndex = currentStep - 2; // 0-based into subRows

    const canContinue = (() => {
        if (secretExpired) return false;
        switch (currentId) {
            case "path":
                // Only GA4 can proceed today — ThriveStack is selectable so its preview
                // can be seen, but it's "coming soon", so Continue stays disabled for it.
                return state.setupPath === "ga4";
            case "connect":
                return isDemoMode
                    ? state.connected && state.propertyIds.length > 0
                    : state.connected && state.propertyIds.length > 0 && airbyteAuthorised;
            case "channel":
                return state.channelFixed || state.channelSkipped || state.channelOutcome === "OK";
            case "events": {
                // The merged events+value step is only complete once GA4 has at least one
                // key event and the user has chosen which count — the no-events phase blocks.
                const noKeys = isDemoMode
                    ? state.devNoKeyEvents
                    : Boolean(setupDetails?.keyEvents) && !setupDetails?.keyEvents?.hasAnyKeyEvents;
                if (noKeys || state.selectedEvents.length === 0) return false;
                // §8 — a selected event with no GA4 revenue must be given a deal value first,
                // otherwise Continue stays disabled (revenue can't be attributed to a $0 event).
                return selectedEventsNeedingValue(state, setupDetails, isDemoMode).every(
                    (name) => (state.eventValues[name] ?? 0) > 0
                );
            }
            case "next":
                return Boolean(syncStartedAt);
            default:
                return true;
        }
    })();

    // ambient glow drift, contained to the card
    const reduced = usePrefersReducedMotion();
    const cardRef = useRef<HTMLDivElement | null>(null);
    const g1Ref = useRef<HTMLDivElement | null>(null);
    const g2Ref = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        if (reduced) return;
        const card = cardRef.current;
        if (!card) return;
        let tx = 0.5;
        let ty = 0.4;
        let cx = 0.5;
        let cy = 0.4;
        let raf = 0;
        const onMove = (e: MouseEvent) => {
            const r = card.getBoundingClientRect();
            tx = (e.clientX - r.left) / Math.max(r.width, 1);
            ty = (e.clientY - r.top) / Math.max(r.height, 1);
        };
        const loop = () => {
            cx += (tx - cx) * 0.05;
            cy += (ty - cy) * 0.05;
            const dx = cx - 0.5;
            const dy = cy - 0.4;
            if (g1Ref.current) g1Ref.current.style.transform = `translate3d(${dx * 40}px, ${dy * 34}px, 0)`;
            if (g2Ref.current) g2Ref.current.style.transform = `translate3d(${dx * -32}px, ${dy * -24}px, 0)`;
            raf = window.requestAnimationFrame(loop);
        };
        window.addEventListener("mousemove", onMove, { passive: true });
        raf = window.requestAnimationFrame(loop);
        return () => {
            window.removeEventListener("mousemove", onMove);
            window.cancelAnimationFrame(raf);
        };
    }, [reduced]);

    // "Finish" only belongs on the genuine last step. On the path chooser the row
    // list is momentarily just [path] until a route is picked, so guard against it
    // masquerading as the final step ("Go to report" on step 1).
    const isFinishStep = isLastStep && currentId !== "path";
    // "Finish", not "Go to report" — the report only fills in once the first sync lands.
    const footerPrimaryLabel =
        currentId === "comingSoon" ? COPY.wizard.close : isFinishStep ? "Finish" : COPY.wizard.next;
    const onPrimary = currentId === "comingSoon" ? onClose : isFinishStep ? handleFinish : next;

    return (
        <AiVisibilityModalOverlay onBackdropClick={onClose}>
            <div ref={cardRef} className="rasx rasx-card">
                <div className="rasx-bg" aria-hidden>
                    <div className="dots" />
                    <div className="glow g1" ref={g1Ref} />
                    <div className="glow g2" ref={g2Ref} />
                </div>

                <header className="rasx-head">
                    <div className="rasx-head-row">
                        <span className="rasx-eyebrow">
                            <span className="rasx-livedot" />
                            {COPY.wizard.title}
                        </span>
                        <button type="button" className="rasx-close" aria-label="Close" onClick={onClose}>
                            <X size={16} strokeWidth={2} />
                        </button>
                    </div>

                    {showStepper && (
                        <div className="rasx-stepper">
                            {subRows.map((row, i) => {
                                const passed = i < activeSubIndex;
                                // A skipped channel check is passed-but-not-complete: show it amber
                                // with a warning marker rather than a done check.
                                const skipped = passed && row.id === "channel" && state.channelSkipped;
                                const stateClass = skipped
                                    ? "skipped"
                                    : passed
                                    ? "done"
                                    : i === activeSubIndex
                                    ? "active"
                                    : "";
                                const reachable = i <= activeSubIndex;
                                return (
                                    <button
                                        key={row.id}
                                        type="button"
                                        disabled={!reachable}
                                        onClick={() => reachable && goTo(i + 2)}
                                        className={`rasx-stepper-item ${stateClass}${reachable ? " clickable" : ""}`}
                                    >
                                        <span className="rasx-stepper-bar">
                                            <span />
                                        </span>
                                        <span className="rasx-stepper-label">
                                            <span className="rasx-stepper-num">
                                                {skipped ? (
                                                    <AlertTriangle strokeWidth={2.5} />
                                                ) : passed ? (
                                                    <Check strokeWidth={3} />
                                                ) : (
                                                    i + 1
                                                )}
                                            </span>
                                            <span className="txt">{row.label}</span>
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </header>

                <div className={`rasx-scroll${currentId === "path" ? " is-path" : ""}`}>
                    {secretExpired && (
                        <div className="rasx-step" style={{ marginBottom: 4 }}>
                            <div className="rasx-status warn">
                                <div className="rasx-status-head">
                                    <AlertTriangle className="ic" size={16} strokeWidth={2} />
                                    <span className="rasx-status-title">{COPY.secretExpired.title}</span>
                                </div>
                                <p className="rasx-status-body">{COPY.secretExpired.body}</p>
                                <button
                                    type="button"
                                    onClick={() => onAuthoriseSync?.()}
                                    disabled={authorising}
                                    className="rasx-btn accent"
                                    style={{ marginTop: 12 }}
                                >
                                    {authorising && <Loader2 className="spin" size={14} />}
                                    {authorising ? COPY.wizard.step1.authorising : COPY.secretExpired.cta}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Keyed so each step change replays the entrance animation. */}
                    <React.Fragment key={currentId}>
                        {currentId === "path" && (
                            <PathStep
                                state={state}
                                patch={patch}
                                revenueStackAvailable={isDemoMode}
                                requestOpen={requestOpen}
                                onToggleRequest={() => setRequestOpen((v) => !v)}
                            />
                        )}
                        {currentId === "connect" && (
                            <ConnectStep
                                state={state}
                                patch={patch}
                                oauthError={oauthError}
                                exchanging={exchanging}
                                setupDetails={setupDetails}
                                detailsLoading={detailsLoading}
                                onSelectProperty={onSelectProperty}
                                onAuthoriseSync={onAuthoriseSync}
                                authorising={authorising}
                                airbyteAuthorised={airbyteAuthorised}
                            />
                        )}
                        {currentId === "channel" && (
                            <ChannelStep state={state} patch={patch} setupDetails={setupDetails} />
                        )}
                        {currentId === "events" && (
                            <RevenueEventsStep
                                state={state}
                                patch={patch}
                                setupDetails={setupDetails}
                                detailsLoading={detailsLoading}
                                onSelectProperty={onSelectProperty}
                            />
                        )}
                        {currentId === "next" && (
                            <WhatsNextStep
                                state={state}
                                defaultEmail={defaultNotifyEmail}
                                onStartSync={onStartSync}
                                syncing={syncing}
                                syncStartedAt={syncStartedAt}
                                connectionExisted={connectionExisted}
                            />
                        )}
                        {currentId === "comingSoon" && (
                            <div className="rasx-step">
                                <StepIntro
                                    eyebrow={COPY.revenueStack.comingSoon.label}
                                    title={COPY.revenueStack.comingSoon.heading}
                                    lede={COPY.revenueStack.comingSoon.body}
                                />
                                <RevenueStackComingSoon />
                            </div>
                        )}
                        {currentId === "script" && (
                            <div className="rasx-step">
                                <StepIntro
                                    eyebrow={COPY.revenueStack.script.label}
                                    title={COPY.revenueStack.script.heading}
                                    lede={COPY.revenueStack.script.subheading}
                                />
                                <RevenueStackScriptStep />
                            </div>
                        )}
                        {currentId === "sessionId" && (
                            <div className="rasx-step">
                                <StepIntro
                                    eyebrow={COPY.revenueStack.sessionId.label}
                                    title={COPY.revenueStack.sessionId.heading}
                                    lede={COPY.revenueStack.sessionId.subheading}
                                />
                                <RevenueStackSessionIdStep />
                            </div>
                        )}
                        {currentId === "stripe" && (
                            <div className="rasx-step">
                                <StepIntro
                                    eyebrow={COPY.revenueStack.stripe.label}
                                    title={COPY.revenueStack.stripe.heading}
                                    lede={COPY.revenueStack.stripe.subheading}
                                />
                                <RevenueStackStripeStep />
                            </div>
                        )}
                    </React.Fragment>
                </div>

                <footer className="rasx-foot">
                    {currentStep > 1 ? (
                        <button type="button" onClick={back} className="rasx-ghost">
                            {COPY.wizard.back}
                        </button>
                    ) : (
                        <span />
                    )}
                    <button type="button" className="rasx-cta" disabled={!canContinue} onClick={onPrimary}>
                        {footerPrimaryLabel}
                        {currentId !== "comingSoon" && <Arrow />}
                    </button>
                </footer>

                {isDemoMode && <DemoControls currentId={currentId} state={state} patch={patch} />}
            </div>
        </AiVisibilityModalOverlay>
    );
}

export default RevenueAttributionSetupWizardV2;
